import React, { useMemo, useState } from 'react';
import { getBlocks, getImageUrl } from '../shared/templateSectionUtils.jsx';

const themed = (theme = {}) => ({
  primary: '#07111f',
  accent: '#0b0f16',
  background: '#e6e8eb',
  color: '#0b0f16',
  mutedColor: '#5f6670',
  headingFontFamily: 'Inter, Arial, sans-serif',
  pageWidth: '1120px',
  sectionSpacing: '72px 24px',
  imageRadius: '24px',
  ...theme
});

const imageUrlOf = (item = {}) => (
  getImageUrl(item)
  || item.imageUrl
  || item.imageURL
  || item.imageSrc
  || item.displayLink
  || item.storedLink
  || item.src
  || item.image
  || item.url
  || item.asset?.displayLink
  || item.asset?.storedLink
  || item.asset?.url
  || ''
);

const toSlide = (item = {}, fallback = {}) => {
  const imageUrl = imageUrlOf(item);
  if (!imageUrl) return null;
  return {
    ...fallback,
    ...item,
    imageUrl,
    title: item.title || item.heading || item.label || fallback.title || '',
    description: item.description || item.body || item.subtitle || fallback.description || '',
    badge: item.badge || item.category || fallback.badge || 'Article',
    tag: item.tag || item.type || fallback.tag || 'IT'
  };
};

const directSlides = (content = {}) => [
  toSlide({
    imageUrl: content.leftImageUrl || content.leftImageURL || content.leftImage || content.imageLeftUrl || content.imageLeft,
    imageContent: content.leftImageContent,
    title: content.leftTitle,
    description: content.leftDescription,
    badge: content.leftBadge,
    tag: content.leftTag
  }),
  toSlide({
    imageUrl: content.centerImageUrl || content.centerImageURL || content.centerImage || content.coverImageUrl || content.coverImage || content.imageUrl,
    imageContent: content.centerImageContent || content.coverImageContent || content.imageContent,
    title: content.centerTitle || content.cardTitle || content.title,
    description: content.centerDescription || content.cardDescription || content.body || content.subtitle,
    badge: content.centerBadge || content.badge,
    tag: content.centerTag || content.tag
  }),
  toSlide({
    imageUrl: content.rightImageUrl || content.rightImageURL || content.rightImage || content.imageRightUrl || content.imageRight,
    imageContent: content.rightImageContent,
    title: content.rightTitle,
    description: content.rightDescription,
    badge: content.rightBadge,
    tag: content.rightTag
  }),
  toSlide({ imageUrl: content.secondaryImageUrl || content.imageUrl2 || content.image2, imageContent: content.secondaryImageContent, title: content.secondaryTitle, description: content.secondaryDescription, badge: content.secondaryBadge, tag: content.secondaryTag }),
  toSlide({ imageUrl: content.tertiaryImageUrl || content.imageUrl3 || content.image3, imageContent: content.tertiaryImageContent, title: content.tertiaryTitle, description: content.tertiaryDescription, badge: content.tertiaryBadge, tag: content.tertiaryTag })
].filter(Boolean);

const slidesFor = (section = {}) => {
  const content = section.content || {};
  const collectionKeys = ['blocks', 'images', 'imageGallery', 'galleryImages', 'slides', 'carouselItems', 'items'];
  const collectionSlides = collectionKeys.flatMap((key) => {
    const values = key === 'blocks' ? getBlocks(section) : content[key];
    return Array.isArray(values) ? values.map((item) => toSlide(item)).filter(Boolean) : [];
  });
  const byUrl = new Map();
  [...directSlides(content), ...collectionSlides].forEach((slide) => {
    if (slide?.imageUrl && !byUrl.has(slide.imageUrl)) byUrl.set(slide.imageUrl, slide);
  });
  return [...byUrl.values()];
};

const WaasImageGalleryCarouselSection = ({ section, theme }) => {
  const t = themed(theme);
  const slides = useMemo(() => slidesFor(section), [section]);
  const [index, setIndex] = useState(0);
  const active = slides[index] || {};
  const left = slides.length > 1 ? slides[(index - 1 + slides.length) % slides.length] : active;
  const right = slides.length > 1 ? slides[(index + 1) % slides.length] : active;
  const canSlide = slides.length > 1;
  const move = (step) => canSlide && setIndex((current) => (current + step + slides.length) % slides.length);

  return (
    <section style={{ backgroundColor: section.style?.backgroundColor || t.background, color: section.style?.color || t.color, padding: section.style?.padding || t.sectionSpacing, overflow: 'hidden' }}>
      <div style={{ maxWidth: section.layout?.width === 'full' ? 'none' : section.style?.maxWidth || t.pageWidth, margin: '0 auto', width: '100%' }}>
        <div className="waas-template-grid" style={{ display: 'grid', gridTemplateColumns: '.95fr 1.05fr', gap: 56, alignItems: 'center' }}>
          <div>
            {section.content?.eyebrow && <p style={{ margin: '0 0 18px', color: section.style?.accentColor || t.primary, fontSize: 12, fontWeight: 800, letterSpacing: '.14em', textTransform: 'uppercase' }}>{section.content.eyebrow}</p>}
            <h2 style={{ margin: 0, fontFamily: t.headingFontFamily, fontWeight: t.headingWeight || 300, color: t.accent, fontSize: 'clamp(44px, 5.2vw, 68px)', lineHeight: .96 }}>{section.content?.title}</h2>
            {section.content?.subtitle && <p style={{ maxWidth: 430, margin: '28px 0 34px', color: t.mutedColor, fontSize: 15, lineHeight: 1.58 }}>{section.content.subtitle}</p>}
            <div style={{ display: 'flex', gap: 16 }}>
              <button type="button" aria-label="Previous insight" disabled={!canSlide} onClick={() => move(-1)} style={{ width: 54, height: 54, border: 0, borderRadius: 999, background: '#ffffff', boxShadow: '0 16px 30px rgba(24,34,46,.08)', color: canSlide ? t.accent : '#94a3b8', cursor: canSlide ? 'pointer' : 'not-allowed', fontSize: 24 }}>{'<'}</button>
              <button type="button" aria-label="Next insight" disabled={!canSlide} onClick={() => move(1)} style={{ width: 54, height: 54, border: 0, borderRadius: 999, background: '#ffffff', boxShadow: '0 16px 30px rgba(24,34,46,.08)', color: canSlide ? t.accent : '#94a3b8', cursor: canSlide ? 'pointer' : 'not-allowed', fontSize: 24 }}>{'>'}</button>
            </div>
          </div>
          <div style={{ position: 'relative', minHeight: 400 }}>
            {left.imageUrl && <img src={left.imageUrl} alt="" aria-hidden="true" style={{ position: 'absolute', left: 0, top: 72, width: 230, height: 300, objectFit: 'cover', borderRadius: 22, opacity: .45, filter: 'saturate(.8)', transform: 'translateX(-22px) scale(.92)', boxShadow: '0 18px 46px rgba(24,34,46,.14)' }} />}
            {right.imageUrl && <img src={right.imageUrl} alt="" aria-hidden="true" style={{ position: 'absolute', right: 0, top: 72, width: 230, height: 300, objectFit: 'cover', borderRadius: 22, opacity: .45, filter: 'saturate(.8)', transform: 'translateX(22px) scale(.92)', boxShadow: '0 18px 46px rgba(24,34,46,.14)' }} />}
            <div style={{ position: 'absolute', inset: '30px 24px 26px 68px', borderRadius: 26, background: '#b8c3da', opacity: .85 }} />
            <article style={{ position: 'relative', width: 'min(370px, 100%)', minHeight: 398, margin: '0 auto', border: '8px solid rgba(255,255,255,.92)', borderRadius: t.imageRadius || 26, overflow: 'hidden', boxShadow: '0 28px 62px rgba(24,34,46,.22)', background: '#ffffff' }}>
              {active.imageUrl && <img src={active.imageUrl} alt={active.title || section.content?.imageAlt || ''} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />}
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(11,15,22,.02), rgba(11,15,22,.68))' }} />
              <div style={{ position: 'absolute', left: 24, right: 24, bottom: 24, color: '#ffffff' }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
                  <span style={{ borderRadius: 999, background: '#ffffff', color: t.accent, padding: '6px 12px', fontSize: 11, fontWeight: 800 }}>{active.badge || 'Article'}</span>
                  <span style={{ borderRadius: 999, background: '#ffffff', color: t.accent, padding: '6px 12px', fontSize: 11, fontWeight: 800 }}>{active.tag || 'IT'}</span>
                </div>
                <h3 style={{ margin: '0 0 10px', fontSize: 30, lineHeight: 1.05, fontWeight: 500 }}>{active.title}</h3>
                <p style={{ margin: 0, fontSize: 13, lineHeight: 1.45 }}>{active.description}</p>
              </div>
            </article>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WaasImageGalleryCarouselSection;
